<template>
<b-tab no-body lazy>
    <template slot="title">
        Export Patient (BETA)
    </template>
    <div v-if="checkUserPermission('export', 'can_execute')" class="mb-3">
        <b-overlay
            :show="exporting"
            rounded
            opacity="0.6"
            spinner-small
            spinner-variant="primary">
            <div class="d-flex justify-content-end align-items-center mb-2">
                <span class="small text-muted mr-2">Date range (optional):</span>
                <b-form-datepicker size="sm" style="max-width: 160px;" v-model="fromDate" :max="toDate" placeholder="From..." reset-button :locale="currentLang" />
                <span class="mx-1">–</span>
                <b-form-datepicker size="sm" style="max-width: 160px;" v-model="toDate" :min="fromDate" placeholder="To..." reset-button :locale="currentLang" />
            </div>
            <div class="d-flex">
                <b-form-radio-group
                    class="ml-auto"
                    v-model="episodes"
                    :options="episodeOptions"
                    buttons
                    button-variant="light"
                >
                <b-dropdown :disabled="exporting" variant="outline-primary" split-variant="primary" right split text="Select" @click="exportPatient">
                    <template #button-content>
                        <i :class="{'fa-fade' : exporting}" class="fa-regular fa-file-export"></i> Export
                    </template>
                    <b-dropdown-item href="#" v-for="episode in patientEpisodes" :key="episode.id" @click="exportByEpisode(episode.id)">Episode: {{ episode.name }}</b-dropdown-item>
                </b-dropdown>
                </b-form-radio-group>
            </div>
        </b-overlay>
    </div>
    <b-table
        ref="table"
        class="m-0"
        responsive
        stacked="sm"
        show-empty
        striped
        hover
        fixed
        small
        primary-key="id"
        :current-page="currentPage"
        :per-page="perPage"
        :items="items"
        :fields="fields"
        :filter-function="filter"
        :busy.sync="$store.getters['loading/isLoading']('export_patient')"
        :no-sort-reset="true"
        :sort-by.sync="sortedBy"
        :sort-desc.sync="sortedDesc"
        :no-border-collapse="false">

        <div slot="table-busy" class="text-center text-danger my-2">
            <b-spinner class="align-middle"></b-spinner>
            <strong>Loading...</strong>
        </div>

        <template v-slot:empty="scope">
            <!-- {{ scope.emptyText }} -->
            <div class="small text-muted text-center my-2">
                <span>n/a</span>
            </div>
        </template>

        <template v-slot:cell(actions)="row">
            <template v-if="row.item.ready">
                <b-button :disabled="row.item.downloaded" v-if="checkUserPermission('export', 'can_execute')" size="sm" variant="primary" @click="download(row.item)"><i class="fa-regular fa-file-arrow-down"></i></b-button>
            </template>
            <template v-else>
                <b-spinner small variant="success" label="Processing"></b-spinner>
            </template>
        </template>
        <template v-slot:custom-foot="scope">
            <b-tr class="border-bottom">
                <b-td class="align-middle" variant="light">
                    Showing {{ startIndex }} to {{ endIndex }} of {{ totalRows }} export
                </b-td>
                <b-td colspan="2" class="text-right align-middle" variant="light">
                    <b-pagination class="m-0" prev-text="Prev" next-text="Next" hide-goto-end-buttons align="right" :total-rows="totalRows" v-model="currentPage" :per-page="perPage" />
                </b-td>
            </b-tr>
        </template>
    </b-table>
</b-tab>
</template>
<script>
import Vue from 'vue'
import { mapState, mapGetters, mapActions } from 'vuex'
import TabExtension from "../TabExtension"
import _ from 'lodash'

export default TabExtension.extend({
    name: 'export-patient',
    data() {
        return {
            exporting: false,
            fields: [
                { key: 'created', label: 'Date/Time', class: 'small align-middle', formatter: (value) => this.toLocaleDate(value) },
                { key: 'user.username', label: 'By', class: 'small align-middle' },                
                { key: "actions", label: "Download", class: "small align-middle text-right" },
            ],

            currentPage: 1,
            perPage: 10,
            sortedBy: 'id',
            sortedDesc: true,
            isBusy: false,

            episodes: 'all',
            fromDate: null,
            toDate: null,
        }
    },
    computed: {
        ...mapGetters('patients', ['getPatientEpisodes', 'getPatientExports']),
        patientEpisodes() {
            return this.getPatientEpisodes()
        },
        items() {
            return this.getPatientExports()
        },
        totalRows() {
            return this.items.length
        },
        startIndex() {
            return (this.currentPage - 1) * this.perPage + 1
        },
        endIndex() {
            var end = this.totalRows
            if (this.perPage < this.totalRows) {
                end = this.perPage * this.currentPage
                if (end > this.totalRows) {
                    end = this.totalRows
                }
            }
            return end
        },
        episodeOptions() {
            return [
              { text: 'All', value: 'all' },
              { text: 'Latest', value: '1' },
              { text: 'Last 2', value: '2' },
              { text: 'Last 3', value: '3' }
            ]
        }
    },
    methods: {
        ...mapActions('patients', ['getEpisodesForCurrentPatient', 'getExportsForCurrentPatient', 'exportCurrentPatient', 'getRunningExportsForCurrentPatient']),
        ...mapActions('interchange', ['downloadExport']),
        async loadForPatient(patient) {
            this.exporting = await this.getRunningExportsForCurrentPatient()
            await this.getExportsForCurrentPatient()
            await this.getEpisodesForCurrentPatient()
        },
        filter() {
            this.$refs.table.refresh()
        },
        async exportPatient() {
            this.exporting = true
            await this.exportCurrentPatient({ type: 'pdf', episodes: this.episodes, from_date: this.fromDate, to_date: this.toDate })
        },
        async exportByEpisode(episode_id) {
            this.exporting = true
            await this.exportCurrentPatient({ type: 'pdf', episodes: episode_id, from_date: this.fromDate, to_date: this.toDate })
        },
        async handleSocket(payload) {
            if (payload.action === "export"){
                if (payload.username === this.user?.username) {
                    await this.getExportsForCurrentPatient()
                    this.exporting = false
                }
            }
        },
        async download(item) {
            await this.downloadExport(item.id)
            await this.getExportsForCurrentPatient()
        }
    },
    async mounted() {
        EventBus.$on('interchange-socket', payload => {
            this.handleSocket(payload)
        }) 
    },
    beforeDestroy() {
        EventBus.$off('interchange-socket')
    },
})
</script>